import MembershipRepository from '@/domains/memberships/api/MembershipRepository'
import MembershipItemRepository from '@/domains/memberships/api/MembershipItemRepository'

const getDefaultState = () => ({
  list: null,
  loading: false
})

export default {
  namespaced: true,
  state: getDefaultState(),

  mutations: {
    resetState(state) {
      Object.assign(state, getDefaultState())
    },
    setLoading(state, loading) {
      state.loading = loading
    },
    setList(state, list) {
      state.list = Array.isArray(list) ? list : []
    },
    replaceOne(state, item) {
      const id = item?.r_membership_id
      if (id == null)
        return
      if (state.list == null)
        return

      const idx = state.list.findIndex(
        m => m.r_membership_id === id
      )
      if (idx >= 0) {
        state.list.splice(idx, 1, item)
      } else {
        state.list.push(item)
      }
    },
    removeOne(state, membershipId) {
      if (state.list == null)
        return
      
      const idx = state.list.findIndex(
        m => m.r_membership_id === membershipId
      )
      if (idx >= 0) state.list.splice(idx, 1)
    }
  },

  actions: {
    resetState({ commit }) {
      commit('resetState')
    },

    /**
     * Create a new membership assignment and add to list.
     * @param {Object} payload - { r_membership_template_id, start_date, r_organization_participant_id }
     * @returns {Promise<Object>} Created membership
     */
    async createMembership({ commit }, payload) {
      const membership = await MembershipRepository.createMembership(payload)
      commit('replaceOne', membership)
      return membership
    },

    /**
     * Fetch memberships with optional date filter and onlyActive flag.
     * @param {Object} payload - { startDate, endDate, onlyActive }
     */
    async fetchMemberships({ commit }, { startDate = null, endDate = null, onlyActive = false } = {}) {
      commit('setLoading', true)
      try {
        const list = await MembershipRepository.getAllMemberships(startDate, endDate, onlyActive)
        commit('setList', list)
        return list
      } finally {
        commit('setLoading', false)
      }
    },

    /**
     * Set paid status and update list from response.
     */
    async setPaid({ commit }, { membershipId, paid }) {
      const updated = await MembershipItemRepository.setPaid(membershipId, paid)
      commit('replaceOne', updated)
      return updated
    },

    /**
     * Update membership (end_date, initial_event_count, price, etc.) and update list.
     */
    async updateMembership({ commit }, { membershipId, data }) {
      const updated = await MembershipItemRepository.updateMembership(membershipId, data)
      commit('replaceOne', updated)
      return updated
    },

    /**
     * Stop membership and update list from response.
     */
    async stopMembership({ commit }, { membershipId, stopDate }) {
      const updated = await MembershipItemRepository.stopMembership(membershipId, stopDate)
      commit('replaceOne', updated)
      return updated
    },

    /**
     * Delete membership and remove from list.
     */
    async deleteMembership({ commit }, { membershipId }) {
      await MembershipItemRepository.deleteMembership(membershipId)
      commit('removeOne', membershipId)
    }
  },

  getters: {
    memberships: state => state.list,
    loading: state => state.loading,
    getMembershipById: state => id => {
      if (id == null) return null
      const numId = typeof id === 'number' ? id : parseInt(id, 10)
      if (Number.isNaN(numId)) return null
      return state.list?.find(m => m.r_membership_id === numId) || null
    }
  }
}
